/**
 * Selection List Component
 * Displays selected nodes with descendant indicators and remove buttons
 */

import React from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import type { NodeTypeDto, SelectionListProps } from '../types';
import { getNodeTypeColor, getNodeTypeName } from '../utils';

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
`;

const SelectionCount = styled.div.withConfig({
  shouldForwardProp: prop => (prop as string) !== 'isFull',
})<{ isFull?: boolean }>`
  font-size: 0.875rem;
  color: ${props =>
    props.isFull ? props.theme?.errorColor || '#f85149' : props.theme?.textColor || '#e6edf3'};
  font-weight: 500;
`;

const ClearButton = styled.button`
  padding: 0.375rem 0.75rem;
  background: transparent;
  color: ${props => props.theme?.textColorHl || '#58a6ff'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.panelBackgroundHover || 'rgba(255, 255, 255, 0.05)'};
    border-color: ${props => props.theme?.textColorHl || '#58a6ff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
`;

const SelectionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.75rem;
  padding: 0.75rem;
  align-items: center;
  border-bottom: 1px solid ${props => props.theme?.borderColor || '#30363d'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.theme?.panelBackgroundHover || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const NodeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

const NodeTypeBadge = styled.span.withConfig({
  shouldForwardProp: prop => (prop as string) !== 'nodeTypeColor',
})<{ nodeTypeColor: string }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: ${props => props.nodeTypeColor}33;
  color: ${props => props.nodeTypeColor};
  border: 1px solid ${props => props.nodeTypeColor}66;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: monospace;
  flex-shrink: 0;
`;

const NodeName = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DescendantIndicator = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme?.labelColor || '#8b949e'};
  font-style: italic;
  flex-shrink: 0;
`;

const RemoveButton = styled.button`
  padding: 0.25rem 0.5rem;
  background: transparent;
  color: ${props => props.theme?.errorColor || '#f85149'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.theme?.errorColor || '#f85149'}22;
    border-color: ${props => props.theme?.errorColor || '#f85149'};
  }
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${props => props.theme?.labelColor || '#8b949e'};
  font-size: 0.875rem;
`;

// ============================================================================
// Component
// ============================================================================

export const SelectionList: FC<SelectionListProps & { nodeTypes?: NodeTypeDto[] }> = ({
  selections,
  maxSelections,
  onRemove,
  onClearAll,
  nodeTypes,
}) => {
  const selectionArray = Array.from(selections.values());
  const isFull = selectionArray.length >= maxSelections;

  return (
    <Container>
      <Header>
        <SelectionCount isFull={isFull} data-testid='loadseer-selected-count'>
          {selectionArray.length} selected
          {isFull && ' (limit reached)'}
        </SelectionCount>
        {selectionArray.length > 0 && (
          <ClearButton onClick={onClearAll} aria-label='Clear all selections'>
            Clear All
          </ClearButton>
        )}
      </Header>

      <ListContainer>
        {selectionArray.length === 0 ? (
          <EmptyState>No nodes selected. Search and select nodes above.</EmptyState>
        ) : (
          selectionArray.map(entry => {
            const nodeTypeColor = getNodeTypeColor(entry.nodeType);
            const nodeTypeName = getNodeTypeName(entry.nodeType, nodeTypes);

            return (
              <SelectionRow key={entry.lsid}>
                <NodeInfo>
                  <NodeTypeBadge nodeTypeColor={nodeTypeColor} title={nodeTypeName}>
                    {entry.nodeType}
                  </NodeTypeBadge>
                  <NodeName title={entry.name}>{entry.name}</NodeName>
                </NodeInfo>

                {entry.includeDescendants && (
                  <DescendantIndicator title='Descendants will be included'>
                    + descendants
                    {entry.descendantNodeType && ` (${entry.descendantNodeType})`}
                  </DescendantIndicator>
                )}

                <RemoveButton
                  onClick={() => onRemove(entry.lsid)}
                  aria-label={`Remove ${entry.name}`}
                >
                  Remove
                </RemoveButton>
              </SelectionRow>
            );
          })
        )}
      </ListContainer>
    </Container>
  );
};
